// Static reference card — the 0–5 proficiency scale definitions. Shared
// between the admin Skill Catalog page (context for the taxonomy owner) and
// "My Skills" (context for the person actually picking a value): "applies
// everywhere ratings are captured" per its own copy, so it's one component,
// not two copies drifting apart. No API backing; these values are
// product-defined, not tenant-configurable, in this phase.
const SCALE_DEFINITIONS: Record<
  'skill' | 'application',
  { level: number; label: string; text: string }[]
> = {
  skill: [
    { level: 0, label: 'No experience', text: 'Has never worked with this skill.' },
    {
      level: 1,
      label: 'Awareness',
      text: 'Know the basics but have little/no hands-on experience.',
    },
    { level: 2, label: 'Basic', text: 'Can perform simple tasks with guidance.' },
    { level: 3, label: 'Proficient', text: 'Can perform routine work independently.' },
    {
      level: 4,
      label: 'Advanced',
      text: "Can handle complex work, exceptions, and review others' work.",
    },
    {
      level: 5,
      label: 'SME',
      text: 'Go-to expert who handles complex matters, trains others, and improves processes.',
    },
  ],
  application: [
    { level: 0, label: 'No experience', text: 'Has never used this application.' },
    {
      level: 1,
      label: 'Awareness',
      text: 'Understands what the application is used for; limited/no hands-on usage.',
    },
    {
      level: 2,
      label: 'Basic User',
      text: 'Can perform basic functions with instructions or SOPs.',
    },
    {
      level: 3,
      label: 'Proficient',
      text: 'Can independently perform regular tasks and workflows.',
    },
    {
      level: 4,
      label: 'Advanced',
      text: 'Can handle advanced features, troubleshoot common issues, and support/review other users.',
    },
    {
      level: 5,
      label: 'SME',
      text: 'Deep knowledge — can train others, troubleshoot complex issues, configure workflows, and recommend process improvements.',
    },
  ],
};

/** 0-5 labels only (no description text) — used by the rating control. */
export const PROFICIENCY_LEVEL_LABELS: Record<'skill' | 'application', Record<number, string>> = {
  skill: Object.fromEntries(SCALE_DEFINITIONS.skill.map((d) => [d.level, d.label])),
  application: Object.fromEntries(SCALE_DEFINITIONS.application.map((d) => [d.level, d.label])),
};

export function ProficiencyScaleReference() {
  return (
    <div className="rounded-md border bg-card p-6">
      <h2 className="text-sm font-semibold">Proficiency scale reference</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Applies everywhere ratings are captured — which scale applies depends on a skill's Type,
        below. 0 flags someone with zero prior exposure to the skill or application.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {(['skill', 'application'] as const).map((t) => (
          <div key={t} className="rounded-md border bg-muted/30 p-4">
            <h3 className="text-xs font-semibold">
              {t === 'skill' ? 'Skills Matrix' : 'Apps Knowledge'}
            </h3>
            <ul className="mt-2 space-y-2">
              {SCALE_DEFINITIONS[t].map((d) => (
                <li key={d.level} className="flex gap-2 text-xs">
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-border bg-background font-medium">
                    {d.level}
                  </span>
                  <span>
                    <span className="font-medium">{d.label}</span>{' '}
                    <span className="text-muted-foreground">{d.text}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
